const SCRIPT_URL = 'https://accounts.google.com/gsi/client';

export interface GoogleCredentialResponse {
    credential?: string;
}

interface GoogleButtonOptions {
    theme: 'outline' | 'filled_blue' | 'filled_black';
    size: 'small' | 'medium' | 'large';
    text: 'signin_with' | 'signup_with' | 'continue_with';
    shape: 'rectangular' | 'pill';
    width?: number;
}

interface GoogleIdApi {
    initialize(options: {
        client_id: string;
        callback: (response: GoogleCredentialResponse) => void;
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
    }): void;
    renderButton(parent: HTMLElement, options: GoogleButtonOptions): void;
    disableAutoSelect(): void;
}

declare global {
    interface Window {
        google?: {accounts: {id: GoogleIdApi}};
    }
}

let pendingLoad: Promise<GoogleIdApi> | null = null;

function currentApi(): GoogleIdApi | undefined {
    return window.google?.accounts?.id;
}

export function loadGoogleIdentity(): Promise<GoogleIdApi> {
    const loaded = currentApi();
    if (loaded !== undefined) {
        return Promise.resolve(loaded);
    }

    if (pendingLoad === null) {
        pendingLoad = new Promise<GoogleIdApi>((resolve, reject) => {
            const fail = (message: string) => {
                pendingLoad = null;
                reject(new Error(message));
            };

            const script = document.createElement('script');
            script.src = SCRIPT_URL;
            script.async = true;
            script.defer = true;
            script.onload = () => {
                const api = currentApi();
                if (api === undefined) {
                    fail('Google Identity Services did not initialise');
                    return;
                }
                resolve(api);
            };
            script.onerror = () => fail('Google Identity Services could not be loaded');

            document.head.appendChild(script);
        });
    }

    return pendingLoad;
}

export function disableGoogleAutoSelect(): void {
    currentApi()?.disableAutoSelect();
}
